import { z } from 'zod'

const SalesSheetMimeTypeSchema = z.union([
  z.literal('text/plain'),
  z.literal('application/vnd.openxmlformats-officedocument.wordprocessingml.document'),
])

const BASE64_REGEX = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/

const MIME_TYPE_BY_EXTENSION = {
  txt: 'text/plain',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
} as const

const getFileExtension = (fileName: string) => {
  const extension = fileName.toLowerCase().split('.').pop()
  return extension && extension !== fileName.toLowerCase() ? extension : null
}

export const SalesSheetSchema = z
  .object({
    fileName: z
      .string()
      .min(1, 'File name is required')
      .refine(fileName => !/[\\/]/.test(fileName), 'File name must not contain path separators')
      .refine(fileName => getFileExtension(fileName) !== null, {
        message: 'File name must include an extension',
      }),
    mimeType: SalesSheetMimeTypeSchema,
    contentBase64: z
      .string()
      .min(1, 'File content is required')
      .refine(content => BASE64_REGEX.test(content), {
        message: 'File content must be valid base64',
      }),
  })
  .superRefine((salesSheet, ctx) => {
    const extension = getFileExtension(salesSheet.fileName)
    if (!extension) return

    const expectedMimeType =
      // biome-ignore lint/nursery/noUnsafeTypeAssertion: RETIRED, do not copy. Fix the source type or narrow the value when touching this.
      MIME_TYPE_BY_EXTENSION[extension as keyof typeof MIME_TYPE_BY_EXTENSION]

    if (!expectedMimeType) {
      ctx.addIssue({
        code: 'custom',
        path: ['fileName'],
        message: 'Only .txt and .docx files are supported',
      })
      return
    }

    if (salesSheet.mimeType !== expectedMimeType) {
      ctx.addIssue({
        code: 'custom',
        path: ['mimeType'],
        message: `MIME type must match the file extension (${expectedMimeType})`,
      })
    }
  })

export type SalesSheet = z.infer<typeof SalesSheetSchema>
